import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from './LanguageContext';
function Counter({
  end,
  suffix = '',
  duration = 2
}: {
  end: number;
  suffix?: string;
  duration?: number;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (isInView) {
      let startTime: number;
      let animationFrame: number;
      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = (timestamp - startTime) / (duration * 1000);
        if (progress < 1) {
          setCount(Math.floor(end * progress));
          animationFrame = requestAnimationFrame(animate);
        } else {
          setCount(end);
        }
      };
      animationFrame = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(animationFrame);
    }
  }, [isInView, end, duration]);
  return <span ref={ref} data-id="element-269">
      {count.toLocaleString()}
      {suffix}
    </span>;
}
export function Technology() {
  const {
    t
  } = useLanguage();
  return <section className="py-32 bg-[#112A32] relative overflow-hidden text-white" data-id="element-270">
      {/* Subtle grid pattern overlay */}
      <div className="absolute inset-0 bg-grid-pattern opacity-20 z-0" data-id="element-271" />

      {/* Glowing orbs */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-bodyon-teal/20 rounded-full blur-[150px] z-0" data-id="element-272" />
      <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-bodyon-mint/10 rounded-full blur-[150px] z-0" data-id="element-273" />

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-274">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center" data-id="element-275">
          {/* Left: Massive Stats */}
          <div className="lg:col-span-5 flex flex-col gap-16" data-id="element-276">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-277">
              <div className="font-heading font-bold text-7xl md:text-8xl text-bodyon-mint tracking-tighter leading-none mb-2" data-id="element-278">
                <Counter end={15000} suffix="+" data-id="element-279" />
              </div>
              <div className="text-xl text-gray-300 font-light uppercase tracking-widest" data-id="element-280">
                {t('tech.stat1.label')}
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} data-id="element-281">
              <div className="font-heading font-bold text-7xl md:text-8xl text-white tracking-tighter leading-none mb-2" data-id="element-282">
                <Counter end={98} suffix="%" data-id="element-283" />
              </div>
              <div className="text-xl text-gray-300 font-light uppercase tracking-widest" data-id="element-284">
                {t('tech.stat2.label')}
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} data-id="element-285">
              <div className="font-heading font-bold text-7xl md:text-8xl text-bodyon-teal tracking-tighter leading-none mb-2" data-id="element-286">
                <Counter end={0} data-id="element-287" />
              </div>
              <div className="text-xl text-gray-300 font-light uppercase tracking-widest" data-id="element-288">
                {t('tech.stat3.label')}
              </div>
            </motion.div>
          </div>

          {/* Right: Editorial Text & Visual */}
          <div className="lg:col-span-7 relative" data-id="element-289">
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-white/5 backdrop-blur-md border border-white/10 p-10 md:p-16 rounded-[40px] relative overflow-hidden" data-id="element-290">
              <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full border border-white/30 text-white/80 font-medium tracking-[0.2em] uppercase text-xs mb-10" data-id="element-291">
                <span className="w-2 h-2 rounded-full bg-bodyon-mint animate-pulse" data-id="element-292"></span>
                {t('tech.title')}
              </div>

              <h2 className="font-heading font-bold text-4xl md:text-6xl text-white mb-8 leading-[1.1] tracking-tight" data-id="element-293">
                {t('tech.heading')}
              </h2>

              <p className="text-xl md:text-2xl text-gray-300 leading-relaxed font-light mb-12" data-id="element-294">
                {t('tech.desc')}
              </p>

              {/* Abstract Tech Visualization */}
              <div className="absolute -bottom-32 -right-32 w-96 h-96 opacity-30 pointer-events-none" data-id="element-295">
                <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 60,
                repeat: Infinity,
                ease: 'linear'
              }} className="w-full h-full" data-id="element-296">
                  <img src="https://cdn.magicpatterns.com/uploads/on22uAviriffrmYHXbYAJ8/ISO_ERGOCENTER_1.svg" alt="Tech Pattern" className="w-full h-full" data-id="element-297" />
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}